import styled from '@emotion/styled';

interface RankedBarsProps {
  items: { label: string; value: number; detail?: string }[];
  formatValue?: (value: number) => string;
  limit?: number;
  emptyText?: string;
}

// A ranked list with inline bars: easier to read than a pie for "which
// category/provider is biggest", and it never clips at small widths.
const RankedBars: React.FC<RankedBarsProps> = ({
  items,
  formatValue = (v) => v.toLocaleString('en-US'),
  limit = 6,
  emptyText = 'No data for this period',
}) => {
  const sorted = [...items].sort((a, b) => b.value - a.value);
  const shown = sorted.slice(0, limit);
  const rest = sorted.slice(limit).reduce((sum, item) => sum + item.value, 0);
  const total = sorted.reduce((sum, item) => sum + item.value, 0);
  const max = shown[0]?.value || 1;

  if (!shown.length || total === 0) return <Empty>{emptyText}</Empty>;

  return (
    <List>
      {shown.map((item) => (
        <Row key={item.label}>
          <div className='top'>
            <span className='label'>{item.label}</span>
            <span className='value'>
              {formatValue(item.value)}
              <span className='share'>{Math.round((item.value / total) * 100)}%</span>
            </span>
          </div>
          <Track>
            <Fill style={{ width: `${Math.max(2, (item.value / max) * 100)}%` }} />
          </Track>
          {item.detail && <div className='detail'>{item.detail}</div>}
        </Row>
      ))}
      {rest > 0 && (
        <More>
          + {sorted.length - limit} more · {formatValue(rest)}
        </More>
      )}
    </List>
  );
};

export default RankedBars;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Row = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  & .top {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    font-size: 13.5px;
  }
  & .label {
    color: var(--ink-2);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  & .value {
    display: inline-flex;
    gap: 8px;
    font-weight: 600;
    color: var(--ink);
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }
  & .share {
    min-width: 34px;
    text-align: right;
    font-weight: 400;
    color: #7a8279;
  }
  & .detail {
    font-size: 12px;
    color: #7a8279;
  }
`;

const Track = styled.div`
  height: 6px;
  border-radius: 999px;
  background: #efece4;
  overflow: hidden;
`;

const Fill = styled.div`
  height: 100%;
  border-radius: 999px;
  background: #1f7a45;
`;

const More = styled.div`
  font-size: 12.5px;
  color: #7a8279;
`;

const Empty = styled.div`
  padding: 24px 0;
  text-align: center;
  font-size: 13px;
  color: #7a8279;
`;
